import { Component, ChangeDetectorRef, ElementRef } from '@angular/core';
import { DialogBase, M27Dialog, DialogButton } from '@m27/common';
import { SnapshotService, XmlService } from '@reporting/services';
import { ReportType, IPlantingReportExportLineItem, INotPlantedReportsLineItem, IPlantingReportExportRequest, INotPlantedReportExportRequest} from '@reporting/interfaces';
import { INotPlantedXmlExportResult, IXmlExportResult, IPlantingXmlExportResult } from '@reporting/interfaces/exports';

@Component({
    selector: 'reporting-dialog-report-xml-result',
    templateUrl: './dialog-report-xml-result.component.html',
    styleUrls: ['./dialog-report-xml-result.component.scss']
})
export class ReportXmlResultDialogComponent extends DialogBase implements M27Dialog {

    public loading = true;
    public xmlResult: IXmlExportResult = null;
    public xmlForce: IXmlExportResult = null;
    public snapshotVerbiage = '';
    public snapshotStatus = '';
    public processComplete = false;
    public forcing = false;
    private reportType: ReportType;
    private lineItemData: IPlantingReportExportLineItem[] | INotPlantedReportsLineItem[];
    private exportReportsWithNoChanges = false;
    private isEfile = false;
    private createSnapshots = false;
    public showForceXmlButton = false;

    constructor(private xmlService: XmlService, private snapshotService: SnapshotService, cd: ChangeDetectorRef, elRef: ElementRef) {
        super(cd, elRef);
    }

    public init(): void {
        this.snapshotVerbiage = '';
        this.snapshotStatus = '';
        this.xmlResult = null;
        this.xmlForce = null;
        this.forcing = false;
        this.processComplete = false;
    }

    public onDialogReady(): void {
    }

    private setupButtons(): void {
        this.rightButtons.push(new DialogButton({
            IsCancel: false,
            IsDisabled: () => !this.processComplete,
            Text: 'Close',
            IsPrimary: () => true,
            Action: (dialogBase: DialogBase) => {
                this.close();
            }
        } as DialogButton));
    }

    public open(reportType: ReportType, lineItemData: IPlantingReportExportLineItem[] | INotPlantedReportsLineItem[], exportReportsWithNoChanges: boolean, createSnapshots: boolean, isEfile: boolean) {
        this.init();

        this.reportType = reportType;
        this.lineItemData = lineItemData;
        this.exportReportsWithNoChanges = exportReportsWithNoChanges;
        this.isEfile = isEfile;
        this.createSnapshots = createSnapshots;
        this.showForceXmlButton = false;

        this.onOpen.emit(false);
        this.modal.open();
        this.setupButtons();
        this.loading = true;
        this.snapshotVerbiage = createSnapshots ? 'Snapshots will be created once files are created.' : 'User elected to not create snapshots.';
        this.snapshotStatus = createSnapshots ? 'waiting' : 'information';

        if (reportType === ReportType.Planting) {
            this.exportPlantingReport(lineItemData as IPlantingReportExportLineItem[], exportReportsWithNoChanges, createSnapshots, this.isEfile);
        }
        else if (reportType === ReportType.NotPlanted) {
            this.exportNotPlantedReport(lineItemData as INotPlantedReportsLineItem[], exportReportsWithNoChanges, createSnapshots, this.isEfile);
        }
    }

    private exportPlantingReport(lineItemData: IPlantingReportExportLineItem[], exportReportsWithNoChanges: boolean, createSnapshots: boolean, isEfile: boolean, forceXmlCreation = false) {
        const exportRequest: IPlantingReportExportRequest = {
            ExportReportsWithNoChanges: exportReportsWithNoChanges,
            Results: lineItemData,
            ForceXMLCreation: forceXmlCreation,
            isEfile: isEfile
        };

        this.xmlService.ExportToXmlPlantingReport(exportRequest)
            .subscribe((data: IPlantingXmlExportResult) => {

                this.xmlForce = data;
                this.xmlResult = data;

                //const permitsThatHaveErrorsAndChanges = this.xmlResult.ErrorsInReports.filter(e => !this.xmlResult.NoChangeReports.includes(e));

                //if (permitsThatHaveErrorsAndChanges.length > 0 || (exportReportsWithNoChanges && this.xmlResult.ErrorsInReports.length > 0)) {
                //    this.showForceXmlButton = true;
                //}

                if (this.xmlResult.ErrorsInReports.length > 0) {
                    this.showForceXmlButton = true;
                }

                if (data.ReportLineItems !== null && data.ReportLineItems.length > 0) {
                    if (createSnapshots) {
                        this.snapshotStatus = 'processing';
                        this.snapshotVerbiage = 'Creating snapshots...';
                        let filteredSnapshots = data.ReportLineItems;
                        if (exportRequest.ForceXMLCreation) {
                            data.ReportLineItems.map(p => p.ForceXmlSnapshotCreation = exportRequest.ForceXMLCreation);
                            filteredSnapshots = filteredSnapshots.filter(r => this.xmlResult.ErrorsInReports.includes(r.PermitNameAgency.toString()));
                        }

                        this.snapshotService.CreateSnapshotPlantingReport(filteredSnapshots).subscribe((created: boolean) => {
                            if (created) {
                                this.snapshotVerbiage = 'Snapshots were created for permits with changes.';
                                this.snapshotStatus = 'success';
                            }
                            else {
                                this.snapshotVerbiage = 'Snapshots were not created; no changes were found.';
                                this.snapshotStatus = 'warning';
                            }

                            this.processComplete = true;
                        });
                    }
                    else {
                        this.processComplete = true;
                    }
                }
                else {
                    if (createSnapshots) {
                        this.snapshotVerbiage = 'No snapshots were created.';
                        this.snapshotStatus = 'warning';

                        if (data.ExceptionMessage !== null) {
                            this.snapshotVerbiage = 'No snapshots were created due to process exception.';
                            this.snapshotStatus = 'error';
                        }
                        else if (data.NoAccessMessage !== null) {
                            // ???
                        }
                    }

                    this.processComplete = true;
                }
            });
    }

    private exportNotPlantedReport(lineItemData: INotPlantedReportsLineItem[], exportReportsWithNoChanges: boolean, createSnapshots: boolean, isEfile: boolean, forceXmlCreation = false) {
        const exportRequest: INotPlantedReportExportRequest = {
            ExportReportsWithNoChanges: exportReportsWithNoChanges,
            Results: lineItemData,
            ForceXMLCreation: forceXmlCreation,
            isEfile: isEfile
        };

        this.xmlService.ExportToXmlNotPlantedReport(exportRequest)
            .subscribe((data: INotPlantedXmlExportResult) => {
                this.xmlForce = data;
                this.xmlResult = data;

                const permitsThatHaveErrorsAndChanges = this.xmlResult.ErrorsInReports.filter(e => !this.xmlResult.NoChangeReports.includes(e));

                if (permitsThatHaveErrorsAndChanges.length > 0 || (exportReportsWithNoChanges && this.xmlResult.ErrorsInReports.length > 0)) {
                    this.showForceXmlButton = true;
                }

                if (data.ReportLineItems !== null && data.ReportLineItems.length > 0) {
                    if (createSnapshots) {
                        this.snapshotStatus = 'processing';
                        this.snapshotVerbiage = 'Creating snapshots...';

                        let filteredSnapshots = data.ReportLineItems;
                        if (exportRequest.ForceXMLCreation) {
                            data.ReportLineItems.map(p => p.ForceXmlSnapshotCreation = exportRequest.ForceXMLCreation);
                            filteredSnapshots = filteredSnapshots.filter(r => this.xmlResult.ErrorsInReports.includes(r.PermitNameAgency.toString()));
                        }

                        this.snapshotService.CreateSnapshotNotPlantedReport(filteredSnapshots).subscribe((created: boolean) => {
                            if (created) {
                                this.snapshotVerbiage = 'Snapshots were created for permits with changes.';
                                this.snapshotStatus = 'success';
                            }
                            else {
                                this.snapshotVerbiage = 'Snapshots were not created; no changes were found.';
                                this.snapshotStatus = 'warning';
                            }

                            this.processComplete = true;
                        });
                    }
                    else {
                        this.processComplete = true;
                    }
                }
                else {
                    if (createSnapshots) {
                        this.snapshotVerbiage = 'No snapshots were created.';
                        this.snapshotStatus = 'warning';

                        if (data.ExceptionMessage !== null) {
                            this.snapshotVerbiage = 'No snapshots were created due to process exception.';
                            this.snapshotStatus = 'error';
                        }
                        else if (data.NoAccessMessage !== null) {
                            // ???
                        }
                    }

                    this.processComplete = true;
                }
            });
    }

    public forceXmlFile(): void {
        this.forcing = true;
        if (this.reportType === ReportType.Planting) {
            const filteredLineItems = this.lineItemData as IPlantingReportExportLineItem[];
            this.exportPlantingReport(filteredLineItems.filter(r => this.xmlResult.ErrorsInReports.includes(r.PermitNameAgency.toString())) as IPlantingReportExportLineItem[], this.exportReportsWithNoChanges, this.createSnapshots, this.isEfile, true);
        }
        else if (this.reportType === ReportType.NotPlanted) {
            const filteredLineItems = this.lineItemData as INotPlantedReportsLineItem[];
            this.exportNotPlantedReport(filteredLineItems.filter(r => this.xmlResult.ErrorsInReports.includes(r.PermitNameAgency.toString())) as INotPlantedReportsLineItem[], this.exportReportsWithNoChanges, this.createSnapshots, this.isEfile, true);
        }
    }

    onDestroy() {
        //this.processComplete = false;
        //this.forcing = false;
        //this.xmlForce = null;
        //this.xmlResult = null;
        this.clearButtons();
        //this.loading = false;
    }

    canCancel(): boolean {
        return true;
    }
}
